import {
  ProjectTeamEmailMode,
  ReasonableAdjustmentsOptionKind,
} from 'generated/graphql';
import { defaultActorRoles } from 'helpers/actorRoles';
import { validateEmail } from 'helpers/email';
import { validateUrl } from 'helpers/url';
import { EnableDisableValue } from 'views/Company/CompanyView/CompanyView.helpers';
import * as yup from 'yup';
import { TSelectOption } from '@spotted-zebra-uk/ui-components';

export const assessmentExpiryDurationOptions: TSelectOption<number>[] = [
  {
    value: 30,
    label: '30 days',
  },
  {
    value: 60,
    label: '60 days',
  },
  {
    value: 90,
    label: '90 days',
  },
  {
    value: 180,
    label: '180 days',
  },
  {
    value: 365,
    label: '365 days',
  },
];

export const validationSchema = yup.object().shape({
  managerReport: yup
    .object()
    .shape({
      label: yup.string(),
      value: yup.number(),
    })
    .required(),
  candidateReport: yup
    .object()
    .shape({
      label: yup.string(),
      value: yup.number(),
    })
    .required(),
  reasonableAdjustments: yup.string().required(),
  contactCompanyEmail: yup
    .string()
    .test(
      'contactCompanyEmailRequired',
      'This is a required field',
      (value, { parent }) => {
        if (
          parent.reasonableAdjustments ===
            ReasonableAdjustmentsOptionKind.ContactCompanyEmail &&
          !value
        ) {
          return false;
        }
        return true;
      },
    )
    .test(
      'contactCompanyEmailValidation',
      'Invalid email',
      (value, { parent }) => {
        if (
          parent.reasonableAdjustments ===
            ReasonableAdjustmentsOptionKind.ContactCompanyEmail &&
          value
        ) {
          return validateEmail(value);
        }
        return true;
      },
    ),
  contactCompanyWebsiteUrl: yup
    .string()
    .test(
      'contactCompanyWebsiteUrlRequired',
      'This is a required field',
      (value, { parent }) => {
        if (
          parent.reasonableAdjustments ===
            ReasonableAdjustmentsOptionKind.ContactCompanyUrl &&
          !value
        ) {
          return false;
        }
        return true;
      },
    )
    .test(
      'contactCompanyWebsiteUrlValidation',
      'Invalid URL',
      (value, { parent }) => {
        if (
          parent.reasonableAdjustments ===
            ReasonableAdjustmentsOptionKind.ContactCompanyUrl &&
          value
        ) {
          return validateUrl(value);
        }
        return true;
      },
    ),
  assessmentExpiry: yup.string().required(),
  assessmentExpiryDuration: yup
    .object()
    .shape({
      value: yup.number(),
      label: yup.string(),
    })
    .test(
      'assessmentExpiryDurationRequired',
      'This is a required field',
      (value, { parent }) => {
        if (
          parent.assessmentExpiry === EnableDisableValue.Enabled &&
          (!value || !value.value)
        ) {
          return false;
        }
        return true;
      },
    ),
  projectTeamEmailRoles: yup
    .array()
    .when('projectTeamEmailSettingsMode.value', {
      is: (value: ProjectTeamEmailMode) =>
        value === ProjectTeamEmailMode.ProjectRoleBased ||
        value === ProjectTeamEmailMode.ProjectRoleAndAssigneeBased,
      then: yup
        .array()
        .of(
          yup.object().shape({
            value: yup.string().required(),
          }),
        )
        .test('is-not-empty', 'This is a required field.', (value) =>
          Boolean(value && value.length > 0),
        )
        .test(
          'is-not-all-roles',
          'You cannot select all hiring roles. Remove some hiring roles or choose another setting.',
          (value) => Boolean(value && value.length < defaultActorRoles.length),
        ),
    }),
});

export const assessmentExpiryEnablingNotificationMessages = {
  [EnableDisableValue.Enabled]:
    'Selecting a recommended validity period helps maintain consistency across roles. It does not automatically enforce assessment expiry at the role level.',
  [EnableDisableValue.Disabled]:
    'Disabling this setting removes the option to configure assessment validity periods on roles. It does not change the status of candidates whose assessments may have already expired, they will remain expired.',
};
export const dataTestId = 'assessments-section-form';
